import { AlternativeItineraryValuePropositionType } from './types';
import { parseISO, differenceInHours } from 'date-fns';
import { TripType } from 'generated/graphql';
import FilteredDataBuilder from '../FilteredItinerariesProvider/FilteredDataBuilder';
import { cityAirportsDistance } from './cityAirportsDistance';

const carriersWithAlternative = ['FR', 'RK'];

export const hasCarriers = (itinerary) =>
  itinerary?.legs?.some((leg) =>
    carriersWithAlternative.includes(leg.segments[0]?.carrier?.id)
  );

export const getCarriers = (itinerary) => {
  const carriers = Array.from(
    new Set(
      itinerary?.legs
        .flatMap((leg) => leg.segments)
        .map((segment) => segment?.carrier?.id)
    )
  ).map(String);
  return carriers;
};

export const getTripType = (tripType) => {
  if (tripType === 'ONE_WAY') {
    return TripType.OneWay;
  } else if (tripType === 'ROUND_TRIP') {
    return TripType.RoundTrip;
  } else {
    return TripType.MultipleDestinations;
  }
};

const calculateLuggage = (itinerary) => {
  const itineraryLuggage = itinerary?.legs?.map(
    (leg) => leg.segments[0].baggageCondition
  );

  if (itineraryLuggage?.includes('BASED_ON_AIRLINE')) {
    return 'BASED_ON_AIRLINE';
  } else if (itineraryLuggage?.includes('CABIN_INCLUDED')) {
    return 'CABIN_INCLUDED';
  } else {
    return itineraryLuggage ? 'CHECKIN_INCLUDED' : 'BASED_ON_AIRLINE';
  }
};

const getSegment = (itinerary, index) => itinerary?.legs[index]?.segments[0];

const calculateTripDuration = (itinerary) => {
  const departureSegment = getSegment(itinerary, 0);
  const returnSegment = getSegment(itinerary, 1);

  if (!departureSegment || !returnSegment) {
    return 0;
  }

  const arrivalDate = parseISO(departureSegment.arrivalDate);
  const departureDate = parseISO(returnSegment.departureDate);

  return differenceInHours(departureDate, arrivalDate) || 0;
};

const isPriceLower = (itinerary, alternativeItinerary) =>
  itinerary?.price?.amount &&
  alternativeItinerary?.price?.amount &&
  Math.ceil(itinerary?.price?.amount) >
    Math.ceil(alternativeItinerary?.price?.amount);

const isLuggageDifferent = (itineraryLuggage, alternativeItineraryLuggage) =>
  itineraryLuggage !== alternativeItineraryLuggage &&
  alternativeItineraryLuggage !== 'BASED_ON_AIRLINE';

const isDurationShorter = (itinerary, alternativeItinerary) =>
  calculateTripDuration(alternativeItinerary) >
  calculateTripDuration(itinerary);

const isDifferentAirports = (itinerary, alternativeItinerary) => {
  return !itinerary?.legs.every((leg, legIndex) => {
    return leg?.segments.every((segment, segmentIndex) => {
      return (
        segment.departure?.iata ===
          alternativeItinerary?.legs[legIndex].segments[segmentIndex].departure
            ?.iata &&
        segment.destination?.iata ===
          alternativeItinerary?.legs[legIndex].segments[segmentIndex]
            .destination?.iata
      );
    });
  });
};

const getTotalDistanceToTheCenter = (itinerary) =>
  itinerary?.legs?.reduce((acc, leg) => {
    let distance = 0;
    leg.segments.forEach((segment) => {
      const segmentDepartureIata =
        segment.departure.cityIata || segment.departure.iata;
      const segmentDestinationIata =
        segment.destination.cityIata || segment.destination.iata;
      distance +=
        cityAirportsDistance[segmentDepartureIata]?.[segment.departure.iata];
      distance +=
        cityAirportsDistance[segmentDestinationIata]?.[
          segment.destination.iata
        ];
    });
    return acc + distance;
  }, 0);

const calculateTotalDistanceToTheCenter = (itinerary, alternativeItinerary) => {
  const itineraryDistance = getTotalDistanceToTheCenter(itinerary);
  const alternativeItineraryDistance =
    getTotalDistanceToTheCenter(alternativeItinerary);
  return itineraryDistance - alternativeItineraryDistance;
};

export const getValuePropositionList = (
  itinerary,
  alternativeItinerary,
  withDiscount
) => {
  const valuePropositions: AlternativeItineraryValuePropositionType[] = [];
  const additionalInformation: { airport: number } = { airport: 0 };
  const itineraryLuggage = calculateLuggage(itinerary);
  const alternativeItineraryLuggage = calculateLuggage(alternativeItinerary);

  if (isPriceLower(itinerary, alternativeItinerary)) {
    // implement 'price' value propositon logic for non discount option
    const priceValueProposition = withDiscount ? 'discount' : 'price';
    valuePropositions.push(priceValueProposition);
  }
  if (isLuggageDifferent(itineraryLuggage, alternativeItineraryLuggage)) {
    valuePropositions.push(
      alternativeItineraryLuggage === 'CHECKIN_INCLUDED'
        ? 'checkedLuggage'
        : 'cabinLuggage'
    );
  }
  if (isDifferentAirports(itinerary, alternativeItinerary)) {
    const distanceDifference = calculateTotalDistanceToTheCenter(
      itinerary,
      alternativeItinerary
    );

    if (distanceDifference && distanceDifference > 0) {
      valuePropositions.push('airport');
      additionalInformation.airport = distanceDifference;
    }
  }
  if (isDurationShorter(itinerary, alternativeItinerary)) {
    valuePropositions.push('convenience');
  }

  valuePropositions.push('social');
  return { valuePropositions, additionalInformation };
};

export const calculateFilteredData = (data, filterRequest) => {
  const filteredDataBuilder = FilteredDataBuilder(data, filterRequest);

  data.itineraries.forEach((itinerary) => filteredDataBuilder.add(itinerary));

  return filteredDataBuilder.build().itineraries;
};

export const getSearchRequest = (searchRequest) => {
  const segments = searchRequest?.itinerary?.segments.map((segment) => {
    return {
      ...segment,
      departure: {
        geoNodeId: segment.departure.geoNodeId,
        iata: segment.departure.iata,
      },
      destination: {
        geoNodeId: segment.destination.geoNodeId,
        iata: segment.destination.iata,
      },
    };
  });
  const itineraryRequest = { ...searchRequest?.itinerary, segments };
  return {
    buyPath: searchRequest?.buyPath,
    tripType: getTripType(searchRequest?.tripType),
    itinerary: itineraryRequest,
  };
};
